import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Upload, Plus, Pencil, Trash2, MousePointer2, ArrowRight } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function AdminDealerForms() {
  const [forms, setForms] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newProvider, setNewProvider] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const fileRef = useRef(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [txt, setTxt] = useState({ id: null, name: "", body_md: "" });
  const [savingTxt, setSavingTxt] = useState(false);
  const bodyRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const { data } = await api.get("/admin/dealers/forms");
      setForms(data || []);
    } catch (e) { toast.error("Failed to load forms library"); }
  };
  useEffect(() => { load(); api.get("/admin/dealers/forms/text/placeholders").then(({ data }) => setPlaceholders(data || [])).catch(() => {}); }, []);

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    const token = `{{${key}}}`;
    if (!el) { setTxt((t) => ({ ...t, body_md: t.body_md + token })); return; }
    const s = el.selectionStart ?? txt.body_md.length, e = el.selectionEnd ?? s;
    const next = txt.body_md.slice(0, s) + token + txt.body_md.slice(e);
    setTxt((t) => ({ ...t, body_md: next }));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + token.length, s + token.length); });
  };

  const saveText = async () => {
    if (!txt.name.trim() || !txt.body_md.trim()) { toast.error("Add a name and paste the agreement text"); return; }
    setSavingTxt(true);
    try {
      if (txt.id) await api.put(`/admin/dealers/forms/${txt.id}/text`, { name: txt.name.trim(), body_md: txt.body_md });
      else await api.post("/admin/dealers/forms/text", { name: txt.name.trim(), body_md: txt.body_md });
      toast.success(txt.id ? "Agreement updated" : "Agreement created");
      setTxt({ id: null, name: "", body_md: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSavingTxt(false); }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/admin/dealers/forms/${id}/preview.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error("Preview failed"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", newName.trim() || file.name);
      fd.append("provider", newProvider.trim());
      await api.post("/admin/dealers/forms", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(newProvider.trim() ? `${newProvider.trim()} form uploaded` : "Form uploaded");
      setNewName("");
      setNewProvider("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveName = async (id) => {
    try {
      await api.put(`/admin/dealers/forms/${id}`, { name: editingName.trim() });
      toast.success("Renamed");
      setEditingId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Rename failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Any dealer packets referencing this form will have it removed.`)) return;
    try {
      await api.delete(`/admin/dealers/forms/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dealer-forms-page">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Super Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">Dealer forms library</h1>
          <p className="text-sm text-[#6C757D] mt-3 max-w-2xl">
            Paste your Advantage agreement text and the portal formats it, adds the signature block and per-page initials automatically. Upload a PDF only for government or third-party forms (W-9, provider agreements).
          </p>
        </div>

        {/* Paste-text agreement editor */}
        <Card className="p-6 mb-6 border border-[#0B132B]/15 rounded-sm bg-white" data-testid="text-agreement-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">{txt.id ? "Edit agreement text" : "New agreement from text"}</div>
            {txt.id && <button type="button" onClick={() => setTxt({ id: null, name: "", body_md: "" })} className="text-[11px] text-[#6C757D] hover:text-[#0B132B]" data-testid="text-agreement-cancel-edit">Cancel edit</button>}
          </div>
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Agreement name</Label>
          <Input value={txt.name} onChange={(e) => setTxt({ ...txt, name: e.target.value })} placeholder="e.g. Producer Agreement"
            className="mt-1 h-11 rounded-sm border-[#0B132B]/15 max-w-md" data-testid="text-agreement-name" />
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="placeholder-chips">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] self-center mr-1">Insert auto-fill:</span>
            {placeholders.map((p) => (
              <button key={p.key} type="button" onClick={() => insertPlaceholder(p.key)}
                className="px-2 py-0.5 rounded-sm border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[10px] font-semibold text-[#0B132B] hover:bg-[#D4AF37]/25"
                data-testid={`placeholder-${p.key}`}>{p.label}</button>
            ))}
          </div>
          <textarea ref={bodyRef} value={txt.body_md} onChange={(e) => setTxt({ ...txt, body_md: e.target.value })}
            placeholder={"Paste the agreement verbiage here.\n\n## 1. Appointment\nAdvantage Warranty Group appoints {{dealer_name}} located at {{physical_address}}...\n1. Numbered items become a numbered list\n- Dashes become bullets\n**bold** for emphasis"}
            className="mt-3 w-full min-h-[320px] rounded-sm border border-[#0B132B]/15 p-3 text-sm font-mono leading-relaxed focus:outline-none focus:border-[#D4AF37]"
            data-testid="text-agreement-body" />
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] text-[#6C757D]">Signature block (dealer + Advantage countersign) and dealer initials on every page are added automatically.</div>
            <Button onClick={saveText} disabled={savingTxt} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="text-agreement-save">
              {savingTxt ? "Saving…" : txt.id ? "Save changes" : "Create agreement"}
            </Button>
          </div>
        </Card>

        {/* Upload new form */}
        <Card className="p-6 mb-8 border-2 border-dashed border-[#D4AF37] rounded-sm bg-[#D4AF37]/5" data-testid="upload-form-card">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B] mb-3">Upload PDF (W-9 / third-party forms only)</div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Form display name (optional)</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Producer Agreement, W-9, ACH Authorization"
                className="mt-1 h-11 rounded-sm border-[#0B132B]/15" data-testid="form-name-input" />
            </div>
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Provider / Administrator</Label>
              <Input list="provider-suggestions" value={newProvider} onChange={(e) => setNewProvider(e.target.value)}
                placeholder="e.g. Ascent, JM&A, Sing (leave blank for Advantage)"
                className="mt-1 h-11 rounded-sm border-[#0B132B]/15" data-testid="form-provider-input" />
              <datalist id="provider-suggestions">
                <option value="Advantage Warranty Group" />
                <option value="Ascent Automotive" />
                <option value="JM&A Group" />
                <option value="Portfolio" />
                <option value="EasyCare" />
                <option value="Zurich" />
                <option value="Assurant" />
              </datalist>
            </div>
            <label className="shrink-0">
              <input ref={fileRef} type="file" accept=".pdf" onChange={onFile} className="hidden" data-testid="form-file-input" />
              <span className="inline-flex items-center gap-2 h-11 px-6 bg-[#0B132B] text-white rounded-sm cursor-pointer text-sm font-semibold hover:bg-[#1C2541] transition-colors">
                <Upload size={16} /> {uploading ? "Uploading…" : "Upload .pdf"}
              </span>
            </label>
          </div>
        </Card>

        {/* Forms list */}
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Library ({forms.length})</div>
          {forms.length === 0 ? (
            <Card className="p-10 border-dashed border-2 border-[#E9ECEF] rounded-sm text-center bg-white">
              <FileText size={28} className="mx-auto text-[#6C757D]" />
              <div className="mt-2 text-sm text-[#6C757D]">No forms yet. Upload one above.</div>
            </Card>
          ) : (
            forms.map((f) => (
              <Card key={f.id} className="p-5 border-[#E9ECEF] rounded-sm flex flex-wrap items-center gap-4" data-testid={`form-row-${f.id}`}>
                <div className="w-10 h-10 rounded-sm bg-[#0B132B] text-white flex items-center justify-center shrink-0">
                  <FileText size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === f.id ? (
                    <div className="flex gap-2 items-center">
                      <Input value={editingName} onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveName(f.id); if (e.key === "Escape") setEditingId(null); }}
                        className="h-9 rounded-sm text-sm max-w-xs" autoFocus data-testid={`form-rename-input-${f.id}`} />
                      <Button size="sm" onClick={() => saveName(f.id)} className="h-9 rounded-sm bg-[#0B132B] text-white" data-testid={`form-rename-save-${f.id}`}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-9 rounded-sm">Cancel</Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-base font-bold text-[#0B132B] truncate">{f.name}</div>
                        {f.kind === "text" && (
                          <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-white bg-[#0B132B] px-2 py-0.5 rounded-sm" data-testid={`form-kind-text-${f.id}`}>Text agreement</span>
                        )}
                        {f.kind !== "text" && (
                          <button type="button" onClick={async () => { const next = f.capture_mode === "info_sheet" ? "mapped" : "info_sheet"; try { await api.put(`/admin/dealers/forms/${f.id}`, { capture_mode: next }); load(); toast.success(next === "info_sheet" ? "Only signature & date boxes are mapped; details go on the generated info sheet" : "All mapped boxes collect input on the sign page"); } catch { toast.error("Could not update"); } }}
                            title="Toggle how dealer details are captured for this form"
                            className={`text-[9px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-sm border ${f.capture_mode === "info_sheet" ? "text-[#0B132B] bg-[#D4AF37]/15 border-[#D4AF37]/50" : "text-[#6C757D] bg-white border-[#E9ECEF]"}`} data-testid={`form-capture-${f.id}`}>
                            {f.capture_mode === "info_sheet" ? "Signature + date only · info sheet" : "Mapped fields"}
                          </button>
                        )}
                        {f.provider && f.provider !== "Advantage Warranty Group" && (
                          <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-2 py-0.5 rounded-sm" data-testid={`form-provider-${f.id}`}>
                            {f.provider}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6C757D] mt-0.5 truncate">{f.kind === "text" ? "Auto-formatted · signature block + initials generated" : `${f.filename} · ${f.fields_count} field(s) mapped`} · uploaded {fmt(f.uploaded_at)}{f.uploaded_by ? ` by ${f.uploaded_by}` : ""}</div>
                    </>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {f.kind === "text" ? (
                    <>
                      <Button size="sm" onClick={() => { setTxt({ id: f.id, name: f.name, body_md: f.body_md || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="h-9 rounded-sm bg-[#D4AF37] hover:bg-[#B8942F] text-[#0B132B] font-bold" data-testid={`form-edit-text-${f.id}`}>
                        <Pencil size={13} className="mr-1.5" /> Edit text
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openPreview(f.id)} className="h-9 rounded-sm border-[#0B132B]/20" data-testid={`form-preview-${f.id}`}>
                        <FileText size={13} className="mr-1.5" /> Preview PDF
                      </Button>
                    </>
                  ) : (
                  <Link to={`/admin/dealer-forms/${f.id}`}>
                    <Button size="sm" className="h-9 rounded-sm bg-[#D4AF37] hover:bg-[#B8942F] text-[#0B132B] font-bold" data-testid={`form-map-${f.id}`}>
                      <MousePointer2 size={13} className="mr-1.5" /> Map fields
                    </Button>
                  </Link>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setEditingId(f.id); setEditingName(f.name); }}
                    className="h-9 rounded-sm border-[#0B132B]/20" data-testid={`form-rename-${f.id}`}>
                    <Pencil size={13} className="mr-1.5" /> Rename
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(f.id, f.name)}
                    className="h-9 rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid={`form-delete-${f.id}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>

        <p className="mt-6 text-xs text-[#6C757D]">
          Tip: after uploading, click <span className="font-semibold text-[#0B132B]">Map fields</span> to place the signature, text, date, and initial slots on the PDF so the dealer knows exactly what to fill in.
        </p>
      </main>
    </div>
  );
}
